// A fixed input script, replayed against the real map, reduced to one number.
//
// The point is to be runnable anywhere the game runs. `npm run trace` prints it under Node; the
// client exposes it as `__fragout.trace()` in the browser. If the two numbers differ, the shared
// step is not deterministic across those runtimes, and reconciliation will fight the server on one
// of them. The trail is printed per step so a mismatch can be bisected.
//
// What it covers: the character controller, the movement integration, the trigonometry in the aim,
// the exponential in the knockback decay (the script injects shoves so this is actually exercised),
// the weapon counters, and the grenade sweep. Hashes are taken over the exact bit patterns of the
// doubles, not rounded text, so a one bit difference shows up.
//
// What it does not cover: the server's rigid body solver. `previewVsServer` measures that
// separately, because it is an approximation question rather than a determinism question.
import { createEnv, createPlayerBody, createProjectile, freeEnv } from "./physics.js";
import { stepPlayer, placePlayer, resetKit } from "./sim.js";
import { stepBallistic } from "./arc.js";
import { STEP_DT, Kind, KINDS } from "./tuning.js";
export const TRACE_STEPS = 600;
/** The script. Deliberately busy: turning, strafing, jumping, crouching, cooking, throwing. */
export function traceInput(i) {
    const t = i / 60;
    return {
        moveX: ((i >> 4) % 3) - 1,
        moveZ: ((i >> 5) % 3) - 1,
        jump: i % 47 === 0,
        crouch: (i >> 6) % 4 === 0,
        fire: i % 90 > 60,
        kick: i % 131 === 0,
        stab: i % 197 === 0,
        interact: false,
        slot: 1,
        yaw: Math.sin(t * 0.7) * 2.1,
        pitch: Math.sin(t * 0.31) * 0.6,
    };
}
/** A blast shove, so the exponential decay in the step is on the measured path rather than idle. */
export function traceShove(i) {
    if (i % 53 !== 0)
        return null;
    return { px: Math.sin(i) * 7.5, pz: Math.cos(i) * 6.25 };
}
export function traceState() {
    const s = {
        x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, px: 0, pz: 0,
        grounded: false, crouch: false,
        slot: 1, slotReq: 1, kind2: 0, ammo2: 0, kind3: 0, ammo3: 0,
        cookLeft: -1, cookKind: 0, fragCooldown: 0, stabLeft: 0, kickCooldown: 0,
        knifeOut: false, knifeReturn: 0,
        fireHeld: false, kickHeld: false, stabHeld: false, interactHeld: false,
    };
    resetKit(s);
    placePlayer(s, 2, 4, 3);
    return s;
}
/** Readable, for bisecting a mismatch by eye. The hash does not use this. */
export function traceSnapshot(s) {
    return [s.x, s.y, s.z, s.vx, s.vy, s.vz, s.px, s.pz]
        .map((n) => n.toFixed(6)).join(",")
        + `|${s.grounded ? 1 : 0}|${s.cookLeft}|${s.fragCooldown}|${s.stabLeft}`;
}
/** FNV-1a over the exact bytes of each double, so nothing is hidden by rounding. */
class BitHash {
    h = 0x811c9dc5;
    view = new DataView(new ArrayBuffer(8));
    add(n) {
        this.view.setFloat64(0, n);
        for (let b = 0; b < 8; b++) {
            this.h ^= this.view.getUint8(b);
            this.h = Math.imul(this.h, 0x01000193) >>> 0;
        }
    }
    addAll(ns) { for (const n of ns)
        this.add(n); }
    get value() { return (this.h >>> 0).toString(16).padStart(8, "0"); }
}
/** Replay the script and reduce it to a hash. Same inputs, same map, same numbers, everywhere. */
export function replayTrace(R, boxes, steps = TRACE_STEPS) {
    const env = createEnv(R, boxes);
    const s = traceState();
    const pb = createPlayerBody(env, s.x, s.y, s.z);
    const hash = new BitHash();
    const trail = [];
    let knockbackExercised = false;
    for (let i = 0; i < steps; i++) {
        const shove = traceShove(i);
        if (shove) {
            s.px += shove.px;
            s.pz += shove.pz;
        }
        stepPlayer(STEP_DT, s, traceInput(i), { env, pb });
        if (s.px !== 0 || s.pz !== 0)
            knockbackExercised = true;
        hash.addAll([s.x, s.y, s.z, s.vx, s.vy, s.vz, s.px, s.pz]);
        hash.addAll([s.grounded ? 1 : 0, s.cookLeft, s.fragCooldown, s.stabLeft, s.kickCooldown]);
        trail.push(traceSnapshot(s));
    }
    const gh = new BitHash();
    const g = { x: 0, y: 5, z: 0, vx: 9, vy: 3, vz: 7 };
    for (let i = 0; i < steps; i++) {
        stepBallistic(env, g, STEP_DT, Kind.Frag);
        gh.addAll([g.x, g.y, g.z, g.vx, g.vy, g.vz]);
    }
    freeEnv(env);
    return { hash: hash.value, steps, trail, grenadeHash: gh.value, knockbackExercised, end: s };
}
/**
 * Two separate questions, because they have different answers and mixing them hides both.
 *
 * `integration` is whether the preview and the server's body agree while nothing is being touched.
 * It should be near zero, and if it is not the drawn arc is simply wrong.
 *
 * `landing` is the one a player feels: how far the end of the drawn arc sits from the spot the
 * real grenade first hits. The arc deliberately stops at that first contact, so this is the whole
 * promise the preview makes.
 */
export function previewVsServer(R, boxes, kind = Kind.Frag, start = { x: 0, y: 12, z: 0 }, vel = { vx: 14, vy: 4, vz: 3 }, steps = 240) {
    const def = KINDS[kind];
    const spawn = (env) => createProjectile(env, {
        radius: def.radius, restitution: def.restitution, friction: def.friction,
        x: start.x, y: start.y, z: start.z, vx: vel.vx, vy: vel.vy, vz: vel.vz,
    });
    // 1. Integration, with nothing to hit.
    const empty = createEnv(R, []);
    const eb = spawn(empty);
    const eg = { x: start.x, y: start.y, z: start.z, vx: vel.vx, vy: vel.vy, vz: vel.vz };
    let integration = 0;
    for (let i = 0; i < steps; i++) {
        stepBallistic(empty, eg, STEP_DT, kind);
        empty.world.step();
        const t = eb.body.translation();
        integration = Math.max(integration, Math.hypot(t.x - eg.x, t.y - eg.y, t.z - eg.z));
    }
    freeEnv(empty);
    // 2. Where each of them first touches the real map.
    const env = createEnv(R, boxes);
    const body = spawn(env);
    const g = { x: start.x, y: start.y, z: start.z, vx: vel.vx, vy: vel.vy, vz: vel.vz };
    let previewBounceStep = -1, serverContactStep = -1;
    let previewLanding = { x: 0, y: 0, z: 0 }, serverLanding = { x: 0, y: 0, z: 0 };
    for (let i = 0; i < steps; i++) {
        if (previewBounceStep < 0) {
            if (stepBallistic(env, g, STEP_DT, kind)) {
                previewBounceStep = i;
                previewLanding = { x: g.x, y: g.y, z: g.z };
            }
        }
        env.world.step(env.events);
        if (serverContactStep < 0) {
            let touched = false;
            env.events.drainCollisionEvents((h1, h2, started) => {
                if (started && (h1 === body.collider.handle || h2 === body.collider.handle))
                    touched = true;
            });
            if (touched) {
                const t = body.body.translation();
                serverContactStep = i;
                serverLanding = { x: t.x, y: t.y, z: t.z };
            }
        }
        if (previewBounceStep >= 0 && serverContactStep >= 0)
            break;
    }
    freeEnv(env);
    const landing = previewBounceStep >= 0 && serverContactStep >= 0
        ? Math.hypot(previewLanding.x - serverLanding.x, previewLanding.y - serverLanding.y, previewLanding.z - serverLanding.z)
        : Number.NaN;
    return { integration, landing, previewBounceStep, serverContactStep, steps };
}
//# sourceMappingURL=trace.js.map